import CreateButton from "../components/CreateButton"
import Checkbox from '../components/Checkbox';
import EnrollmentAccessBadge from '../components/EnrollmentAccessBadge';
import '@splidejs/react-splide/css';
import { useContext, useEffect, useMemo, useState } from "react";
import Markdown from 'react-markdown';
import { useNavigate, useParams } from "react-router-dom";
import TextareaAutosize from 'react-textarea-autosize';
import rehypeRaw from 'rehype-raw';
import api from "../api";
import { ReactComponent as ReorderIcon } from "../assets/images/icons/ic-reorder.svg";
import MainContext from "../common/MainContext";
import { canAccessPage } from "../common/permissions";
import { CourseType, FileType } from "../common/constants";
import Breadcrumb from "../components/Breadcrumb";
import StatusBadge from '../components/StatusBadge';
import Button from "../components/Button";
import AlertDialog from "../components/dialogs/AlertDialog";
import Dialog from '../components/dialogs/Dialog';
import EditionMailComposer from '../components/EditionMailComposer';
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout";
import Pagination from "../components/Pagination";
import Select from "../components/Select";
import StudentProgress from "../components/StudentProgress";
import Table from '../components/Table';
import TextInput from "../components/TextInput";
import TooltipWrapper from '../components/TooltipWrapper';
import Uploader from "../components/Uploader";
import { DialogStatus } from "../enums";
import typo from "../typography.module.css";
import { formatDateV2, formatTimeV2 } from "../utils";
import styles from "./EditionEditor.module.css";

const EditionEditor = () => {

    const navigate = useNavigate()
    const context = useContext(MainContext)
    const [edition, setEdition] = useState(null)
    const { courseId, editionId } = useParams()
    const [course, setCourse] = useState(null)
    const [alert, setAlert] = useState({ open: false, title: '', text: '', actions: [], status: DialogStatus.Default })
    const [loading, setLoading] = useState(false)
    const [saveDisabled, setSaveDisabled] = useState(true)
    const [uploaderReset, setUploaderReset] = useState(false)
    const [progresses, setProgresses] = useState(null)
    const [filterProgresses, setFilterProgresses] = useState([])
    const [loadingProgresses, setLoadingProgresses] = useState(false)
    const [progressesCount, setProgressesCount] = useState(0)
    const [progressesPage, setProgressesPage] = useState(0)
    const [progressesPer] = useState(25)
    const [studentsSearchQuery, setStudentsSearchQuery] = useState('')
    const [editMode, setEditMode] = useState(editionId === 'create' ? true : false)
    const [studentProgressDetail, setStudentProgressDetail] = useState(null)
    const [modulesOrdering, setModulesOrdering] = useState(false)
    const [originalModulesOrder, setOriginalModulesOrder] = useState(null)

    const canEditCourses = useMemo(() => {
        return canAccessPage(context?.user, 'courses', 'edit')
    }, [context?.user])

    const editionModulesById = useMemo(() => {
        return new Map((edition?.modules || []).map(module => [String(module.id), module.name]))
    }, [edition?.modules])

    const editionModuleNames = useMemo(() => {
        return new Set((edition?.modules || []).map(module => module.name))
    }, [edition?.modules])

    const [status, setStatus] = useState(
        [
            { id: 0, name: 'Bozza', value: 'draft' },
            { id: 1, name: 'Pubblico', value: 'public' },
            { id: 2, name: 'Nascosto', value: 'hidden' },
            { id: 3, name: 'Terminato', value: 'ended' }
        ]
    )

    const types =
        [
            { id: 0, name: 'Live', value: 'live' },
            { id: 1, name: 'On Demand', value: 'on_demand' },
        ]

    const toApiDateTime = (input) => {
        if (!(input instanceof Date)) {
            return null
        }

        const timestamp = input.getTime()
        if (Number.isNaN(timestamp)) {
            return null
        }

        return new Date(timestamp).toISOString().split('.')[0]
    }

    const getOnDemandDefaultDateTime = () => {
        const future = new Date()
        future.setFullYear(future.getFullYear() + 100)
        return future.toISOString().split('.')[0]
    }

    const initializeStatus = (status) => {
        switch (status) {
            case 'draft':
                setStatus([
                    { id: 0, name: 'Bozza', value: 'draft' },
                    { id: 1, name: 'Pubblico', value: 'public' },
                ])
                break
            case 'public':
                setStatus([
                    { id: 1, name: 'Pubblico', value: 'public' },
                    { id: 2, name: 'Nascosto', value: 'hidden' },
                    { id: 3, name: 'Terminato', value: 'ended' }
                ])
                break
            case 'hidden':
                setStatus([
                    { id: 1, name: 'Pubblico', value: 'public' },
                    { id: 2, name: 'Nascosto', value: 'hidden' },
                    { id: 3, name: 'Terminato', value: 'ended' }
                ])
                break
            case 'ended':
                setStatus([
                    { id: 2, name: 'Nascosto', value: 'hidden' },
                    { id: 3, name: 'Terminato', value: 'ended' }
                ])
                break
            default: return
        }
    }

    useEffect(() => {
        const getCourse = async () => {
            try {
                const course = await api.get(`/admin/courses/${courseId}`)
                setCourse(course)
            }
            catch (e) {
                console.error(e)
            }
        }

        if (courseId) {
            getCourse()
        }
    }, [courseId])

    useEffect(() => {
        const getEdition = async (edition_id) => {
            try {
                let edition = await api.get(`/admin/editions/${edition_id}`)
                setEdition(edition)
                initializeStatus(edition.status)
            }
            catch (e) {
                console.error(e)
            }
        }

        if (editionId && editionId !== 'create') {
            getEdition(editionId)
        }

        if (editionId && editionId === 'create') {
            setEdition({
                name: '',
                description: '',
                limit: 30,
                expired_at: null,
                locked_at: null,
                thumbnail: null,
                status: 'draft',
                type: 'live',
                tags: [],
                students: [],
                modules: []
            })
        }
    }, [editionId])

    useEffect(() => {
        if (course && editionId === 'create') {
            setEdition((prev) => {
                prev.thumbnail = course.thumbnail
                return { ...prev }
            })
        }
    }, [course, editionId])

    useEffect(() => {
        if (edition) {
            if (edition.status === 'public') {
                setSaveDisabled(edition.name.trim() === '' || edition.description.trim() === '' || edition.thumbnail.trim() === '')
            }
            else {
                setSaveDisabled(edition.name.trim() === '')
            }
        }
        // eslint-disable-next-line
    }, [edition])

    useEffect(() => {
        if (editionId !== "create" && edition?.id) {
            fetchStudentsProgresses({ page: progressesPage, q: studentsSearchQuery })
        }
        // eslint-disable-next-line
    }, [edition?.id, progressesPage, studentsSearchQuery])

    const fetchStudentsProgresses = async ({ page = progressesPage, q = studentsSearchQuery, persist = true } = {}) => {
        if (!edition?.id) {
            return []
        }

        setLoadingProgresses(true)
        try {
            const params = new URLSearchParams({
                page: String(page),
                per: String(progressesPer),
            })
            if (q?.trim()) {
                params.set('q', q.trim())
            }

            const response = await api.get(`/admin/editions/${edition.id}/progresses?${params.toString()}`, { timeout: 0 })
            const items = response?.progresses || []

            if (persist) {
                setProgresses(items)
                setFilterProgresses(items)
                setProgressesCount(response?.count || 0)
            }

            return items
        }
        catch (e) {
            console.error(e)
            if (persist) {
                setProgresses([])
                setFilterProgresses([])
                setProgressesCount(0)
            }
            return []
        }
        finally {
            setLoadingProgresses(false)
        }
    }

    const setLessonCompleted = async ({ userId, lessonId, mode }) => {
        if (!edition?.id) {
            return null
        }

        try {
            await api.post(`/admin/editions/${edition.id}/students/${userId}/lessons/${lessonId}/complete`, { mode })
            const updatedProgresses = await fetchStudentsProgresses()
            const updatedStudent = updatedProgresses.find(s => s.id === userId)
            setStudentProgressDetail(updatedStudent ?? null)
            return updatedStudent ?? null
        }
        catch (e) {
            console.error(e)
            setAlert({
                open: true,
                title: 'Operazione Fallita',
                text: e.detail,
                status: DialogStatus.Error,
                actions: [
                    {
                        label: 'OK',
                        onClick: () => {
                            setAlert((prev) => {
                                prev.open = false
                                return { ...prev }
                            })
                        }
                    }
                ]
            })
            return null
        }
    }

    const create = async () => {
        setLoading(true)
        try {
            const onDemandDefaultDateTime = edition.type === 'on_demand' ? getOnDemandDefaultDateTime() : null
            const normalizedExpiredAt = edition.expired_at || onDemandDefaultDateTime
            const normalizedLockedAt = edition.locked_at || onDemandDefaultDateTime

            const newEdition = {
                course_id: course.id,
                name: edition.name,
                description: edition.description,
                expired_at: normalizedExpiredAt,
                locked_at: normalizedLockedAt,
                type: edition.type,
                limit: edition.limit,
                status: edition.status,
                free_enrollment_enabled: edition.type === 'on_demand' && !!edition.free_enrollment_enabled,
                thumbnail: edition.thumbnail,
                tags: edition.tags?.length > 0 ? JSON.stringify(edition.tags.map(t => t.id)) : null
            }
            const created = await api.post(`/admin/editions`, newEdition)

            setEdition(created)
            initializeStatus(created.status)
            setUploaderReset(!uploaderReset)
            setAlert({
                open: true,
                title: 'Operazione Completata',
                text: 'I dati sono stati aggiornati con successo',
                status: DialogStatus.Success,
                actions: [
                    {
                        label: 'CHIUDI',
                        onClick: () => {
                            setAlert((prev) => {
                                prev.open = false
                                return { ...prev }
                            })
                            navigate(`/courses/${courseId}/editions/${created.id}`)
                        }
                    }
                ]
            })
        }
        catch (e) {
            console.error(e)
            setAlert({
                open: true,
                title: 'Operazione Fallita',
                text: e.detail,
                status: DialogStatus.Error,
                actions: [
                    {
                        label: 'OK',
                        onClick: () => {
                            setAlert((prev) => {
                                prev.open = false
                                return { ...prev }
                            })
                        }
                    }
                ]
            })
        }
        setLoading(false)
    }

    const update = async () => {
        setLoading(true)
        try {
            const onDemandDefaultDateTime = edition.type === 'on_demand' ? getOnDemandDefaultDateTime() : null
            const normalizedExpiredAt = edition.expired_at || onDemandDefaultDateTime
            const normalizedLockedAt = edition.locked_at || onDemandDefaultDateTime

            const updatedEdition = {
                name: edition.name,
                description: edition.description,
                expired_at: normalizedExpiredAt,
                locked_at: normalizedLockedAt,
                limit: edition.limit,
                status: edition.status,
                free_enrollment_enabled: edition.type === 'on_demand' && !!edition.free_enrollment_enabled,
                thumbnail: edition.thumbnail,
                tags: edition.tags?.length > 0 ? JSON.stringify(edition.tags.map(t => t.id)) : null
            }
            const updated = await api.put(`/admin/editions/${edition.id}`, updatedEdition)
            setEdition(updated)
            initializeStatus(updated.status)
            setUploaderReset(!uploaderReset)
            setAlert({
                open: true,
                title: 'Operazione Completata',
                text: 'I dati sono stati aggiornati con successo',
                status: DialogStatus.Success,
                actions: [
                    {
                        label: 'CHIUDI',
                        onClick: () => {
                            setAlert((prev) => {
                                prev.open = false
                                return { ...prev }
                            })
                            setEditMode(false)
                        }
                    }
                ]
            })
        }
        catch (e) {
            console.error(e)
            setAlert({
                open: true,
                title: 'Operazione Fallita',
                text: e.detail,
                status: DialogStatus.Error,
                actions: [
                    {
                        label: 'OK',
                        onClick: () => {
                            setAlert((prev) => {
                                prev.open = false
                                return { ...prev }
                            })
                        }
                    }
                ]
            })
        }
        setLoading(false)
    }

    const save = async () => {
        if (editionId === 'create') {
            await create()
        }
        else {
            const updated = await updateModulePositions()
            if (updated === false) {
                return
            }
            await update()
        }
    }

    const updateModulePositions = async () => {
        try {
            let position = edition.modules.map((module, index) => ({
                module_id: module.id,
                position: index,
            }));
            await api.post('/admin/modules/order', position)
            return true
        }
        catch (e) {
            console.error(e)
            setAlert({
                open: true,
                title: 'Operazione Fallita',
                text: e.detail,
                status: DialogStatus.Error,
                actions: [
                    {
                        label: 'OK',
                        onClick: () => {
                            setAlert((prev) => {
                                prev.open = false
                                return { ...prev }
                            })
                        }
                    }
                ]
            })
            return false
        }
    }

    const toggleModulesOrdering = async () => {
        if (!canEditCourses) {
            return
        }

        if (modulesOrdering) {
            const updated = await updateModulePositions()
            if (updated === false) {
                return
            }
            setOriginalModulesOrder(null)
            setModulesOrdering(false)
            return
        }

        setOriginalModulesOrder((edition?.modules || []).map((module) => ({ ...module })))
        setModulesOrdering(true)
    }

    const cancelModulesOrdering = () => {
        if (!originalModulesOrder) {
            setModulesOrdering(false)
            return
        }

        setEdition((prev) => ({
            ...prev,
            modules: originalModulesOrder.map((module, index) => ({
                ...module,
                position: module.position ?? index,
            }))
        }))
        setOriginalModulesOrder(null)
        setModulesOrdering(false)
    }

    const onStudentsSearch = (q) => {
        setStudentsSearchQuery(q?.trim() || '')
        setProgressesPage(0)
    }

    const setListModules = async (modules) => {
        if (!canEditCourses) {
            return
        }

        let editionClone = { ...edition }
        editionClone.modules = modules.map(({ module_name, lessons_count, duration_label, teachers_count, tutors_count, href, ...module }, index) => ({
            ...module,
            position: index,
        }));
        setEdition(editionClone)
    }

    const getStudentProgressValue = (student) => {
        if (!student) {
            return 0
        }

        const editionType = student.edition_type || edition?.type

        if (editionType === 'live') {
            const duration = Number(student.edition_duration) || 0
            const progress = Number(student.edition_seconds_progress) || 0

            if (duration <= 0) {
                return 0
            }

            const p = Math.round((progress * 100) / duration)
            if (isNaN(p)) {
                return 0
            }
            if (p < 0) {
                return 0
            }
            if (p > 100) {
                return 100
            }
            return p
        }

        const totalLessons = getStudentTotalLessonsValue(student)
        if (totalLessons <= 0) {
            return 0
        }
        const p = Math.round((getStudentViewedLessonsValue(student) / totalLessons) * 100)
        return isNaN(p) ? 0 : p
    }

    const getStudentViewedLessonsValue = (student) => {
        if (!student.modules || !Array.isArray(student.modules)) {
            return 0
        }

        return student.modules.reduce((total, module) => {
            if (module.lessons && Array.isArray(module.lessons)) {
                const validLessons = module.lessons.filter(
                    lesson => lesson.mandatory !== false && lesson.completed
                );
                return total + validLessons.length;
            }
            return total;
        }, 0);
    }

    const getStudentTotalLessonsValue = (student) => {
        if (!student.modules || !Array.isArray(student.modules)) {
            throw new Error("L'oggetto deve contenere un campo 'modules' che è un array.");
        }

        return student.modules.reduce((total, module) => {
            if (module.lessons && Array.isArray(module.lessons)) {
                return total + module.lessons.filter(lesson => lesson.mandatory !== false).length;
            }
            return total;
        }, 0);
    }

    const getLessonLivePercentage = (lesson) => {
        const p = Math.round((lesson?.live_duration || 0) * 100 / (lesson?.duration || 0))
        const clamped = p > 100 ? 100 : p
        return isNaN(clamped) ? 0 : clamped
    }

    const getLessonRecordedPercentage = (lesson) => {
        const p = Math.round((lesson?.recorded_duration || 0) * 100 / (lesson?.video_duration || 0))
        const clamped = p > 100 ? 100 : p
        return isNaN(clamped) ? 0 : clamped
    }

    const formatDurationValue = (seconds) => {
        const totalSeconds = Math.max(0, Math.floor(Number(seconds) || 0))
        const hours = Math.floor(totalSeconds / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const remainingSeconds = totalSeconds % 60

        if (hours > 0) {
            if (minutes > 0) {
                return `${hours}h ${minutes}m`
            }
            return `${hours}h`
        }

        if (minutes > 0) {
            if (remainingSeconds > 0) {
                return `${minutes}m ${remainingSeconds}s`
            }
            return `${minutes}m`
        }

        return `${remainingSeconds}s`
    }

    const getSortedModuleLessons = (module) => {
        return [...(module?.lessons || [])].sort((a, b) => {
            const aTime = a?.starts_at ? new Date(a.starts_at).getTime() : Number.MAX_SAFE_INTEGER
            const bTime = b?.starts_at ? new Date(b.starts_at).getTime() : Number.MAX_SAFE_INTEGER
            return aTime - bTime
        })
    }

    const getModuleLessonsTooltip = (module) => {
        const lessons = getSortedModuleLessons(module)

        if (lessons.length === 0) {
            return <div className={typo.caption}>Nessuna lezione</div>
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', minWidth: '240px' }}>
                {lessons.map((lesson, index) => {
                    const lessonEnd = lesson.ended_at ? lesson.ended_at : lesson.ends_at

                    return (
                        <div key={lesson.id ?? `${module.id}-${index}`} style={{ display: 'flex', flexDirection: 'column', gap: '.125rem' }}>
                            <div>{lesson.name || `Lezione ${index + 1}`}</div>
                            <div>
                                {lesson.starts_at
                                    ? `${formatDateV2(lesson.starts_at, { day: '2-digit', month: '2-digit', year: 'numeric' })}${lessonEnd ? ` • ${formatTimeV2(lesson.starts_at)} - ${formatTimeV2(lessonEnd)}` : ''}`
                                    : 'Data da definire'}
                            </div>
                        </div>
                    )
                })}
            </div>
        )
    }

    const getPeopleTooltip = (people = [], fallbackLabel) => {
        if (!people.length) {
            return <div className={typo.caption}>{fallbackLabel}</div>
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', minWidth: '220px' }}>
                {people.map((person, index) => (
                    <div key={person.id ?? `${person.name}-${index}`}>
                        {[person.name, person.surname].filter(Boolean).join(' ') || person.email || `Elemento ${index + 1}`}
                    </div>
                ))}
            </div>
        )
    }

    const moduleRows = [...(edition?.modules || [])]
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((module, index) => {
            const lessons = getSortedModuleLessons(module)
            const teachers = (module.teachers || []).filter(t => t.role === 'teacher')
            const tutors = (module.teachers || []).filter(t => t.role === 'tutor')

            return {
                ...module,
                position: module.position ?? index,
                href: `/courses/${courseId}/editions/${editionId}/modules/${module.id}`,
                module_name: {
                    value: module.name || '',
                    type: 'string',
                    component: <div>{module.name || 'Senza nome'}</div>
                },
                lessons_count: {
                    value: lessons.length,
                    type: 'number',
                    component: (
                        <TooltipWrapper component={getModuleLessonsTooltip(module)}>
                            <div style={{ whiteSpace: 'nowrap' }}>
                                {lessons.length} {lessons.length === 1 ? 'Lezione' : 'Lezioni'}
                            </div>
                        </TooltipWrapper>
                    )
                },
                duration_label: {
                    value: Number(module.duration) || 0,
                    type: 'number',
                    component: (
                        <div style={{ whiteSpace: 'nowrap' }}>
                            {formatDurationValue(module.duration)}
                        </div>
                    )
                },
                teachers_count: {
                    value: teachers.length,
                    type: 'number',
                    component: (
                        <TooltipWrapper component={getPeopleTooltip(teachers, 'Nessun docente')}>
                            <div style={{ whiteSpace: 'nowrap' }}>
                                {teachers.length} {teachers.length === 1 ? 'Docente' : 'Docenti'}
                            </div>
                        </TooltipWrapper>
                    )
                },
                tutors_count: {
                    value: tutors.length,
                    type: 'number',
                    component: (
                        <TooltipWrapper component={getPeopleTooltip(tutors, 'Nessun tutor')}>
                            <div style={{ whiteSpace: 'nowrap' }}>
                                {tutors.length} {tutors.length === 1 ? 'Tutor' : 'Tutor'}
                            </div>
                        </TooltipWrapper>
                    )
                }
            }
        })

    const exportAllStudentsFrequencyCsv = async () => {
        let students = []
        let exportPage = 0
        let total = 0

        do {
            const params = new URLSearchParams({
                page: String(exportPage),
                per: '100',
            })
            const response = await api.get(`/admin/editions/${edition.id}/progresses?${params.toString()}`, { timeout: 0 })
            const batch = response?.progresses || []
            total = response?.count || batch.length
            students = [...students, ...batch]
            exportPage += 1

            if (batch.length === 0) {
                break
            }
        } while (students.length < total)

        if (!students || students.length === 0) {
            setAlert({
                open: true,
                title: 'Nessun dato da esportare',
                text: 'Non ci sono dati di frequenza disponibili per questa edizione.',
                status: DialogStatus.Error,
                actions: [
                    {
                        label: 'OK',
                        onClick: () => setAlert((prev) => ({ ...prev, open: false }))
                    }
                ]
            })
            return
        }

        const rows = [[
            'student_id',
            'student_name',
            'student_surname',
            'student_email',
            'edition_type',
            'module_name',
            'lesson_id',
            'lesson_name',
            'lesson_type',
            'lesson_date',
            'lesson_hour_range',
            'lesson_live_duration_seconds',
            'lesson_recorded_duration_seconds',
            'student_live_duration_seconds',
            'student_recorded_duration_seconds',
            'student_live_duration_percentage',
            'student_recorded_duration_percentage',
            'completed'
        ]]

        students.forEach((student) => {
            (student.modules || []).forEach((module) => {
                (module.lessons || []).forEach((lesson) => {
                    const livePercentage = getLessonLivePercentage(lesson)
                    const recordedPercentage = getLessonRecordedPercentage(lesson)
                    const isCompleted = livePercentage >= 80 || recordedPercentage >= 90

                    rows.push([
                        student.id,
                        student.name || '',
                        student.surname || '',
                        student.email || '',
                        student.edition_type || edition?.type || '',
                        module.name || '',
                        lesson.id,
                        lesson.name || '',
                        lesson.type || '',
                        lesson.starts_at ? formatDateV2(lesson.starts_at, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '',
                        (lesson.starts_at && (lesson.ended_at || lesson.ends_at)) ? `${formatTimeV2(lesson.starts_at)} - ${formatTimeV2(lesson.ended_at ? lesson.ended_at : lesson.ends_at)}` : '',
                        lesson.duration || 0,
                        lesson.video_duration || 0,
                        lesson.live_duration || 0,
                        lesson.recorded_duration || 0,
                        livePercentage,
                        recordedPercentage,
                        isCompleted ? 'true' : 'false'
                    ])
                })
            })
        })

        const escapeCell = (value) => {
            const stringValue = String(value ?? '')
            if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n')) {
                return `"${stringValue.replace(/"/g, '""')}"`
            }
            return stringValue
        }

        const csv = rows.map(row => row.map(escapeCell).join(',')).join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        const editionName = (edition?.name || `edition_${edition?.id || 'unknown'}`).toLowerCase().replace(/\s+/g, '_')
        const datePart = new Date().toISOString().slice(0, 10)
        link.setAttribute('href', url)
        link.setAttribute('download', `frequenza_studenti_${editionName}_${datePart}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    return (
        <HeaderFooterLayout hideFooter>
            <div className={styles.container}>
                {edition &&
                    <div className={styles.section}>
                        <div className={styles.sectionInner}>
                            <div className={styles.breadcrumb}>
                                <Breadcrumb items={[
                                    { label: 'Corsi', to: '/courses' },
                                    { label: course?.name || 'Corso', to: `/courses/${courseId}` },
                                    { label: editionId === 'create' ? 'Nuova edizione' : edition.name },
                                ]} />
                            </div>
                            {editMode &&
                                <div className={styles.infoSection}>
                                    <div className={styles.modulesHeader}>
                                        <div className={styles.sectionTitle}>INFORMAZIONI</div>
                                        <div style={{ display: 'flex', gap: '1rem' }}>
                                            <Button appearance='subtle' accentColor={'var(--sf-red)'} onClick={() => { setEditMode(false) }}>Annulla</Button>
                                            <Button
                                                disabled={loading || saveDisabled}
                                                onClick={() => {
                                                    save()
                                                }}
                                            >
                                                Salva
                                            </Button>
                                        </div>
                                    </div>
                                    <div className={styles.infoEditorContent}>
                                        <div className={styles.row}>
                                            <div className={styles.column}>
                                                <div className={styles.columnInner}>
                                                    <div className={typo.caption}>NOME</div>
                                                    <TextInput appearance="modern" type="text" value={edition.name} placeholder="Nome" onKeyUp={(value) => {
                                                        setEdition((prev) => {
                                                            prev.name = value
                                                            return { ...prev }
                                                        })
                                                    }} />
                                                </div>

                                                <div className={styles.columnInner}>
                                                    <div className={typo.caption}>STATO</div>
                                                    <Select appearance="modern" style={{ height: 'fit-content' }} selected={status.find(s => s.value === edition.status)} options={status} onSelect={(value) => {
                                                        setEdition((prev) => {
                                                            prev.status = value.value
                                                            return { ...prev }
                                                        })
                                                    }} />
                                                </div>

                                                <div className={styles.columnInner}>
                                                    <div className={typo.caption}>TIPO</div>
                                                    <Select appearance="modern" disabled={edition.id} style={{ height: 'fit-content' }} selected={types.find(s => s.value === edition.type)} options={types} onSelect={(value) => {
                                                        setEdition((prev) => {
                                                            prev.type = value.value

                                                            if (value.value === 'on_demand') {
                                                                const onDemandDefaultDateTime = getOnDemandDefaultDateTime()
                                                                if (!prev.expired_at) {
                                                                    prev.expired_at = onDemandDefaultDateTime
                                                                }
                                                                if (!prev.locked_at) {
                                                                    prev.locked_at = onDemandDefaultDateTime
                                                                }
                                                            }

                                                            return { ...prev }
                                                        })
                                                    }} />
                                                </div>

                                                {edition.type === 'on_demand' && <div className={styles.columnInner}>
                                                    <Checkbox label="Iscrizione gratuita" value={!!edition.free_enrollment_enabled} onChange={value => setEdition(prev => ({ ...prev, free_enrollment_enabled: value }))} />
                                                    <p className={typo.caption}>Consente l’iscrizione con accesso completo per un anno, senza acquisto. Può essere attiva su una sola edizione del corso.</p>
                                                    {edition.free_enrollment_enabled && course?.slug && <a href={`${process.env.REACT_APP_WEBAPP_URL || ''}/my-path/courses/${course.slug}/subscribe`} target="_blank" rel="noreferrer">Apri la pagina di iscrizione gratuita</a>}
                                                </div>}
                                                {edition.type === "live" &&
                                                    <>
                                                        <div className={styles.columnInner}>
                                                            <div className={typo.caption}>LIMITE STUDENTI</div>
                                                            <TextInput appearance="modern" value={edition.limit} placeholder="Limite Studenti" type="number" onKeyUp={(value) => {
                                                                setEdition((prev) => {
                                                                    prev.limit = value
                                                                    return { ...prev }
                                                                })
                                                            }} />
                                                        </div>
                                                        <div className={styles.columnInner}>
                                                            <TextInput appearance="modern"
                                                                type="date"
                                                                value={edition.expired_at ? new Date(edition.expired_at + 'Z') : null}
                                                                defaultValue={edition.expired_at ? new Date(edition.expired_at + 'Z') : null}
                                                                placeholder="Scadenza Iscrizioni"
                                                                onKeyUp={(d) => {
                                                                    const nextDateTime = toApiDateTime(d)
                                                                    if (!nextDateTime) {
                                                                        return
                                                                    }

                                                                    setEdition((prev) => {
                                                                        prev.expired_at = nextDateTime
                                                                        return { ...prev }
                                                                    })
                                                                }}
                                                            />
                                                        </div>
                                                        <div className={styles.columnInner}>
                                                            <TextInput appearance="modern"
                                                                type="date"
                                                                value={edition.locked_at ? new Date(edition.locked_at + 'Z') : null}
                                                                defaultValue={edition.locked_at ? new Date(edition.locked_at + 'Z') : null}
                                                                placeholder="Scadenza Materiale"
                                                                onKeyUp={(d) => {
                                                                    const nextDateTime = toApiDateTime(d)
                                                                    if (!nextDateTime) {
                                                                        return
                                                                    }

                                                                    setEdition((prev) => {
                                                                        prev.locked_at = nextDateTime
                                                                        return { ...prev }
                                                                    })
                                                                }}
                                                            />
                                                        </div>
                                                    </>
                                                }
                                                <div className={styles.columnInner}>
                                                    <div className={typo.caption}>DESCRIZIONE</div>
                                                    <TextareaAutosize
                                                        value={edition.description}
                                                        minRows={5}
                                                        maxRows={10}
                                                        type="text"
                                                        className={styles.textArea}
                                                        placeholder="Descrizione"
                                                        onChange={(e) => {
                                                            const { value } = e.target
                                                            setEdition((prev) => {
                                                                prev.description = value
                                                                return { ...prev }
                                                            })
                                                        }} />
                                                </div>
                                            </div>
                                            <div className={styles.column}>
                                                <div className={styles.columnInner}>
                                                    <div className={typo.caption}>THUMBNAIL</div>
                                                    <TextInput appearance="modern" disabled={edition?.thumbnail?.startsWith('blob:')} type="text" value={edition?.thumbnail} placeholder="Thumbnail URL" onKeyUp={(value) => {
                                                        setEdition((prev) => {
                                                            prev.thumbnail = value ? value : ''
                                                            return { ...prev }
                                                        })
                                                    }} />
                                                    <Uploader
                                                        crop={true}
                                                        preview={edition?.thumbnail}
                                                        reset={uploaderReset}
                                                        multiple={false}
                                                        type={FileType.Image}
                                                        onComplete={(uploads) => {
                                                            setEdition((prev) => {
                                                                prev.thumbnail = uploads[0].url
                                                                return { ...prev }
                                                            })
                                                        }}
                                                        onDelete={() => {
                                                            setEdition((prev) => {
                                                                prev.thumbnail = ""
                                                                return { ...prev }
                                                            })
                                                        }}
                                                    />
                                                    <div className={styles.duration}>
                                                        <div className={typo.caption}>DURATA</div>
                                                        <div className={typo.body}><strong>{formatDurationValue(edition.duration)}</strong></div>
                                                        <div className={typo.caption}>La durata dell'edizione è calcolata automaticamente sulla base della durata dei suoi moduli.</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }
                            {!editMode &&
                                <div className={`${styles.infoSection} ${styles.infoSummary}`}>
                                    <div className={`${styles.info} ${!edition.thumbnail ? styles.withoutThumbnail : ''}`}>
                                        {edition.thumbnail &&
                                            <div className={styles.infoVisual}>
                                                <img src={edition.thumbnail} alt='module-thumbnail' className={styles.infoThumbnail} />
                                            </div>
                                        }
                                        <div className={styles.infoInner}>
                                            <div className={styles.infoHeaderRow}>
                                                <div className={styles.infoTitleBlock}>
                                                    <h1 className={styles.infoName}>{edition.name}</h1>
                                                    {edition.description && <div className={styles.infoDescription}>
                                                        <Markdown rehypePlugins={[rehypeRaw]}>{edition.description}</Markdown>
                                                    </div>}
                                                </div>
                                                <div className={styles.infoRestrictions}>
                                                    {edition.status === 'draft' && <StatusBadge appearance="soft" color={'var(--secondary)'} label={"BOZZA"} />}
                                                    {edition.status === 'public' && <StatusBadge appearance="soft" color={'var(--sf-green)'} label={"PUBBLICA"} />}
                                                    {edition.status === 'hidden' && <StatusBadge appearance="soft" color={'var(--sf-orange)'} label={"NASCOSTA"} />}
                                                    {edition.status === 'ended' && <StatusBadge appearance="soft" color={'rgba(var(--text-color-rgb), 55%)'} label={"TERMINATA"} />}
                                                    <Button appearance="subtle" onClick={() => setEditMode(true)}>Modifica</Button>
                                                </div>
                                            </div>
                                            <div className={styles.infoMetaList}>
                                                <div className={styles.infoMetaRow}>
                                                    <div className={styles.infoMetaLabel}>Tipo</div>
                                                    <div className={styles.infoMetaValue}>{edition.type === 'live' ? 'Live' : 'On Demand'}</div>
                                                </div>
                                                <div className={styles.infoMetaRow}>
                                                    <div className={styles.infoMetaLabel}>Durata</div>
                                                    <div className={styles.infoMetaValue}>{formatDurationValue(edition.duration)}</div>
                                                </div>
                                                {edition.type === 'live' &&
                                                    <>
                                                        <div className={styles.infoMetaRow}>
                                                            <div className={styles.infoMetaLabel}>Scadenza Iscrizioni</div>
                                                            <div className={styles.infoMetaValue}>{formatDateV2(edition.expired_at, { day: 'numeric', month: 'long', year: 'numeric' })} • {formatTimeV2(edition.expired_at)}</div>
                                                        </div>
                                                        <div className={styles.infoMetaRow}>
                                                            <div className={styles.infoMetaLabel}>Scadenza Materiale</div>
                                                            <div className={styles.infoMetaValue}>{formatDateV2(edition.locked_at, { day: 'numeric', month: 'long', year: 'numeric' })} • {formatTimeV2(edition.locked_at)}</div>
                                                        </div>
                                                    </>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }

                            {editionId !== 'create' &&
                                <>
                                    <div className={`${styles.modules} ${styles.tableCard}`}>
                                        <div className={styles.modulesHeader}>
                                            <div className={styles.sectionTitle}>MODULI • {edition.modules.length}</div>
                                            <div style={{ display: 'flex', gap: '.5rem' }}>
                                                {modulesOrdering &&
                                                    <Button
                                                        appearance='subtle'
                                                        accentColor={'var(--sf-red)'}
                                                        onClick={cancelModulesOrdering}
                                                    >
                                                        Annulla
                                                    </Button>
                                                }
                                                <Button
                                                    appearance='subtle'
                                                    disabled={!canEditCourses || edition.modules.length === 0}
                                                    onClick={toggleModulesOrdering}
                                                >
                                                    <ReorderIcon style={{ width: '11px', height: '11px' }} />
                                                    {modulesOrdering ? 'Salva ordine' : 'Riordina'}
                                                </Button>
                                                {!modulesOrdering &&
                                                    <CreateButton onClick={() => {
                                                            if ([CourseType.Masterclass, CourseType.Bootcamp].includes(course.type)) {
                                                                if (edition.modules.length === 0) {
                                                                    navigate(`/courses/${courseId}/editions/${editionId}/modules/create`)
                                                                } else {
                                                                    setAlert({
                                                                        open: true,
                                                                        text: "Le masterclass possono contenere al massimo un modulo.",
                                                                        status: DialogStatus.Error,
                                                                        actions: [
                                                                            {
                                                                                label: 'OK',
                                                                                onClick: () => {
                                                                                    setAlert((prev) => {
                                                                                        prev.open = false
                                                                                        return { ...prev }
                                                                                    })
                                                                                }
                                                                            }
                                                                        ]
                                                                    })
                                                                }
                                                            } else if (course.type === CourseType.Professional) {
                                                                navigate(`/courses/${courseId}/editions/${editionId}/modules/create`)
                                                            }
                                                        }}>

                                                        Aggiungi
                                                    </CreateButton>
                                                }
                                            </div>
                                        </div>
                                        <div className={styles.modulesContent}>
                                            {modulesOrdering &&
                                                <div className={styles.tableToolbar}>Trascina le righe della tabella per riordinare i moduli, poi premi salva ordine.</div>
                                            }
                                            <Table
                                                appearance="minimal"
                                                style={{ border: 0, borderRadius: 0, boxShadow: 'none', marginTop: 0 }}
                                                columns={[
                                                    { key: 'module_name', label: 'Modulo', type: 'custom', style: { padding: '1rem' } },
                                                    { key: 'lessons_count', label: 'Lezioni', type: 'custom', style: { padding: '1rem' } },
                                                    { key: 'duration_label', label: 'Durata', type: 'custom', style: { padding: '1rem' } },
                                                    { key: 'teachers_count', label: 'Docenti', type: 'custom', style: { padding: '1rem' } },
                                                    { key: 'tutors_count', label: 'Tutor', type: 'custom', style: { padding: '1rem' } }
                                                ]}
                                                defaultSort={{ key: 'position', direction: 'asc' }}
                                                ordering={modulesOrdering}
                                                onOrdering={setListModules}
                                                onRowClick={(module) => {
                                                    if (module?.id) {
                                                        navigate(`/courses/${courseId}/editions/${editionId}/modules/${module.id}`)
                                                    }
                                                }}
                                                elements={moduleRows}
                                            />
                                        </div>
                                    </div>

                                    <div className={`${styles.modules} ${styles.tableCard}`}>
                                        <div className={styles.modulesHeader}>
                                            <div className={styles.sectionTitle}>STUDENTI • {progressesCount}</div>
                                            <Button appearance='subtle' onClick={exportAllStudentsFrequencyCsv}>Esporta CSV</Button>
                                        </div>
                                        <div className={styles.modulesContent}>
                                            <div className={styles.tableToolbar}>
                                            <TextInput appearance="modern"
                                                onKeyUp={onStudentsSearch}
                                                type="search"
                                                placeholder={"Cerca..."}
                                                style={{
                                                    backgroundColor: "var(--background-secondary-color)",
                                                }} />
                                            </div>
                                            <Table
                                                appearance="minimal"
                                                style={{ border: 0, borderRadius: 0, boxShadow: 'none', marginTop: 0 }}
                                                columns={[
                                                    { key: 'id', label: 'ID', type: 'number' },
                                                    { key: 'name', label: 'NOME', type: 'string' },
                                                    { key: 'surname', label: 'COGNOME', type: 'string' },
                                                    { key: 'email', label: 'EMAIL', type: 'string' },
                                                    { key: 'enrollment_access', label: 'ACCESSO', type: 'custom' },
                                                    { key: 'enrollment_origin', label: 'ORIGINE', type: 'string' },
                                                    { key: 'allowed_modules', label: 'MODULI ABILITATI', type: 'custom' },
                                                    { key: 'lessons', label: 'LEZIONI', type: 'custom' },
                                                    { key: 'progress', label: 'PROGRESSO', type: 'custom' },
                                                    { key: 'registered_at', label: 'REGISTRATO IL', type: 'datetime' },
                                                    { key: 'last_access', label: 'ULTIMO ACCESSO', type: 'datetime' }
                                                ]}
                                                loading={loadingProgresses}
                                                loadingN={25}
                                                onRowClick={(student) => {
                                                    setStudentProgressDetail(student)
                                                }}
                                                defaultSort={{ key: 'surname', direction: 'asc' }}
                                                elements={filterProgresses.map((student) => {
                                                    const allowedModules = Array.isArray(student.allowed_modules) ? student.allowed_modules : []
                                                    const allowedModuleNames = allowedModules.map((allowedModule) => {
                                                        if (allowedModule && typeof allowedModule === 'object') {
                                                            return allowedModule.name || editionModulesById.get(String(allowedModule.id))
                                                        }

                                                        const value = String(allowedModule)
                                                        return editionModuleNames.has(value) ? value : editionModulesById.get(value)
                                                    }).filter(Boolean)

                                                    return {
                                                        ...student,
                                                        edition_type: edition.type,
                                                        enrollment_origin: student.enrollment_source === 'free' ? 'Iscrizione gratuita' : '—',
                                                        enrollment_access: { value: student.access_mode || 'full', type: 'custom', component: <EnrollmentAccessBadge student={student} /> },
                                                        progress: { value: getStudentProgressValue(student), type: 'number', component: <div style={{ whiteSpace: 'nowrap' }}>{getStudentProgressValue(student)}%</div> },
                                                        lessons: { value: getStudentViewedLessonsValue(student), type: 'number', component: <div style={{ whiteSpace: 'nowrap' }}>{`${getStudentViewedLessonsValue(student)} / ${getStudentTotalLessonsValue(student)}`}</div> },
                                                        last_access: student.last_access,
                                                        allowed_modules: {
                                                            value: allowedModules.length, type: 'custom', component:
                                                                <TooltipWrapper html={allowedModuleNames.length > 0 ? allowedModuleNames.join("<br/>") : null}>
                                                                    <div>{allowedModules.length > 0 ? allowedModules.length : 'Tutti'}</div>
                                                                </TooltipWrapper>
                                                        }
                                                    }
                                                })}
                                            />
                                            <Pagination
                                                page={progressesPage}
                                                total={progressesCount}
                                                per={progressesPer}
                                                onChange={setProgressesPage}
                                                loading={loadingProgresses}
                                            />
                                        </div>
                                    </div>

                                    <div className={styles.modules}>
                                        <div className={styles.modulesHeader}>
                                            <div>
                                                <div className={styles.sectionTitle}>MAIL</div>
                                                <div className={typo.caption}>Scrivi e programma una comunicazione per gli studenti di questa edizione.</div>
                                            </div>
                                        </div>
                                        <div className={styles.modulesContent}>
                                            <EditionMailComposer edition={edition} />
                                        </div>
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                }
            </div >
            <AlertDialog
                open={alert.open}
                title={alert.title}
                text={alert.text}
                actions={alert.actions}
                status={alert.status}
                onClose={() => {
                    setAlert((prev) => {
                        prev.open = false
                        return { ...prev }
                    })
                    setEditMode(false)
                }} />
            {studentProgressDetail &&
                <Dialog appearance="modern" title="Progressi dello studente" open={!!studentProgressDetail} onClose={() => setStudentProgressDetail(null)} style={{ width: 'min(1180px, 100%)' }} >
                    <StudentProgress modern editionName={edition?.name} profileHref={`/users/students/${studentProgressDetail.id}`} student={studentProgressDetail} onSetLessonCompleted={setLessonCompleted} />
                </Dialog>
            }
        </HeaderFooterLayout >
    )

}

export default EditionEditor
